<template>
  <div class="traceability-filter">
    <Card title="Filtros">
      <div class="row mt-n2">
        <SearchFieldSelector
          :options="textSearchOptions"
          :selected-option="selectedOption"
          :date-range-value="dateSelected"
          :text-value="textQuery"
          :default-start-date="defaultStartDate"
          :default-end-date="defaultEndDate"
          class="search-select-options"
          @updateSearchValue="onChangeSearchValue"
          @enterPressed="submitSearch"
        />

        <ButtonBase
          variant="primary"
          :loading="isLoading"
          :disabled="isLoading"
          @click="submitSearch"
        >
          Buscar
        </ButtonBase>
      </div>
    </Card>
  </div>
</template>

<script>
import moment from 'moment'
import Card from '@/components/common/Card.vue'
import SearchFieldSelector from '@/components/common/SearchFieldSelectorV2.vue'
import notify from '@/api/notification'
import {
  TRACEABILITY_SEARCH_PATIENT_NAME,
  TRACEABILITY_SEARCH_OS_ALVARO,
  TRACEABILITY_SEARCH_DATE,
  TRACEABILITY_SEARCH_SAMPLE,
  TRACEABILITY_SEARCH_OS_LIS,
  TRACEABILITY_SEARCH_BATCH,
  TRACEABILITY_SEARCH_NTO,
  TRACEABILITY_SEARCH_PERIOD_DAYS_DEFAULT,
} from '@/enums'
import { mapActions, mapGetters } from 'vuex'

const SEARCH_OPTIONS = [
  {
    id: TRACEABILITY_SEARCH_PATIENT_NAME,
    value: TRACEABILITY_SEARCH_PATIENT_NAME,
    label: 'Nome do Paciente',
    type: 'text',
    placeholder: 'Digite o nome do paciente',
  },
  {
    id: TRACEABILITY_SEARCH_OS_ALVARO,
    value: TRACEABILITY_SEARCH_OS_ALVARO,
    label: 'OS Alvaro',
    type: 'text',
    placeholder: 'Digite o número da OS Alvaro',
  },
  {
    id: TRACEABILITY_SEARCH_OS_LIS,
    value: TRACEABILITY_SEARCH_OS_LIS,
    label: 'OS LIS',
    type: 'text',
    placeholder: 'Digite o número da OS LIS',
  },
  {
    id: TRACEABILITY_SEARCH_SAMPLE,
    value: TRACEABILITY_SEARCH_SAMPLE,
    label: 'Amostra',
    type: 'text',
    placeholder: 'Número da amostra',
  },
  {
    id: TRACEABILITY_SEARCH_BATCH,
    value: TRACEABILITY_SEARCH_BATCH,
    label: 'Lote',
    type: 'text',
    placeholder: 'Digite o número do lote',
  },
  {
    id: TRACEABILITY_SEARCH_NTO,
    value: TRACEABILITY_SEARCH_NTO,
    label: 'NTO',
    type: 'text',
    placeholder: 'Digite o NTO',
  },
  {
    id: TRACEABILITY_SEARCH_DATE,
    value: TRACEABILITY_SEARCH_DATE,
    label: 'Período',
    type: 'date',
    placeholder: 'Selecione um período',
    limitDays: 1827,
  },
]

export default {
  name: 'TraceabilityFilter',

  components: {
    Card,
    SearchFieldSelector,
  },

  data() {
    return {
      today: new Date(),
      selectedOption: null,
      dateSelected: { start: null, end: null },
      textQuery: '',
      filterType: TRACEABILITY_SEARCH_PATIENT_NAME,
      status: null,
      isShowingRangeWarning: false,
      textSearchOptions: SEARCH_OPTIONS,
    }
  },

  computed: {
    ...mapGetters(['traceabilityFilterState', 'traceabilityOrdersLoadingStatus']),

    isLoading() {
      return this.traceabilityOrdersLoadingStatus === 'STATUS_LOADING'
    },

    defaultStartDate() {
      return moment(this.today)
        .subtract(TRACEABILITY_SEARCH_PERIOD_DAYS_DEFAULT, 'days')
        .toDate()
    },

    defaultEndDate() {
      return this.today
    },
  },

  watch: {
    $route(to, from) {
      if (to.path === from.path && to.query !== from.query) {
        this.initializeFromStore()
      }
    },

    traceabilityFilterState: {
      handler(newState) {
        this.updateComponentFromStore(newState)
      },
      deep: true,
    },
  },

  mounted() {
    this.initializeFromStore()
  },

  methods: {
    ...mapActions([
      'getTraceabilityGroupedExams',
      'getTraceabilityGetGroupedExamsCount',
      'selectTraceabilityParams',
      'setCurrentTabTraceability',
    ]),

    initializeFromStore() {
      const storeState = this.traceabilityFilterState
      this.updateComponentFromStore(storeState)
    },

    updateComponentFromStore(storeState) {
      const selectedOption =
        this.findOptionByValue(storeState.typeOfSearch) ||
        this.findOptionByValue(TRACEABILITY_SEARCH_PATIENT_NAME)

      this.setFilterOption(selectedOption, selectedOption.value)

      let textValue = ''
      if (storeState.typeOfSearch === TRACEABILITY_SEARCH_NTO) {
        textValue = storeState.placeExecution || ''
      } else if (storeState.typeOfSearch === TRACEABILITY_SEARCH_DATE) {
        textValue = ''
      } else {
        textValue = storeState.query || ''
      }

      this.setTextQuery(textValue)
      this.setDatesFromStore(storeState)
      this.setStatus(storeState.status)
    },

    // ========== HELPERS ==========

    findOptionByValue(value) {
      return this.textSearchOptions.find(option => option.value === value)
    },

    setFilterOption(option, filterType) {
      this.selectedOption = option
      this.filterType = filterType
    },

    setDefaultDates() {
      this.dateSelected = {
        start: this.defaultStartDate,
        end: this.defaultEndDate,
      }
    },

    setTextQuery(query) {
      this.textQuery = query
    },

    setStatus(status) {
      this.status = status || null
    },

    setDatesFromStore(storeState) {
      if (storeState.startDate && storeState.endDate) {
        this.dateSelected = {
          start: moment(storeState.startDate, 'DD/MM/YYYY').toDate(),
          end: moment(storeState.endDate, 'DD/MM/YYYY').toDate(),
        }
      } else {
        this.setDefaultDates()
      }
    },

    // ========== SEARCH ==========

    async submitSearch() {
      const legacyParams = this.buildLegacyParams()

      legacyParams.status = ''
      this.status = null

      await this.selectTraceabilityParams(legacyParams)
      await this.setCurrentTabTraceability(0)

      this.updateUrlParams(legacyParams)

      await Promise.all([
        this.getTraceabilityGroupedExams({ clearDataFirst: true }),
        this.getTraceabilityGetGroupedExamsCount(),
      ])
    },
    updateUrlParams(params) {
      const query = {}

      if (params.typeOfSearch) query.typeOfSearch = params.typeOfSearch

      if (params.typeOfSearch === TRACEABILITY_SEARCH_DATE) {
        if (params.startDate) query.startDate = params.startDate
        if (params.endDate) query.endDate = params.endDate
      } else {
        if (params.q) query.q = params.q
      }

      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.push({
          path: this.$route.path,
          query: query,
        })
      }
    },

    buildLegacyParams() {
      const params = {
        typeOfSearch: this.filterType,
        status: '',
      }

      if (this.filterType === TRACEABILITY_SEARCH_DATE) {
        params.startDate = this.formatDate(this.dateSelected?.start)
        params.endDate = this.formatDate(this.dateSelected?.end)
        params.q = ''
      } else if (this.filterType === TRACEABILITY_SEARCH_NTO) {
        params.q = this.textQuery
        params.nto = this.textQuery
        params.startDate = null
        params.endDate = null
      } else {
        params.q = this.textQuery
        params.startDate = null
        params.endDate = null
      }

      return params
    },

    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY') : null
    },

    // ========== EVENT HANDLING ==========

    onChangeSearchValue(searchEvent) {
      this.selectedOption = searchEvent

      if (this.isDateTypeSearch(searchEvent)) {
        this.handleDateSearch(searchEvent)
      } else {
        this.handleTextSearch(searchEvent)
      }
    },

    // ========== DATE VALIDATION ==========

    isDateTypeSearch(searchEvent) {
      return searchEvent?.type === 'date'
    },

    handleDateSearch(searchEvent) {
      const { date } = searchEvent

      if (this.isRangeValid(date)) {
        this.setValidDateRange(date)
      } else {
        this.handleInvalidDateRange()
      }
    },

    handleTextSearch(searchEvent) {
      this.textQuery = searchEvent?.query || ''
      this.filterType = searchEvent?.value || TRACEABILITY_SEARCH_PATIENT_NAME
    },

    isRangeValid(date) {
      if (!date.start || !date.end) return false
      const start = moment(date.start)
      const end = moment(date.end)
      return end.diff(start, 'days') <= TRACEABILITY_SEARCH_PERIOD_DAYS_DEFAULT
    },

    setValidDateRange(date) {
      this.dateSelected = date
      this.isShowingRangeWarning = false
      this.filterType = TRACEABILITY_SEARCH_DATE
    },

    // ========== ERROR HANDLING ==========

    handleInvalidDateRange() {
      if (this.isShowingRangeWarning) return

      this.isShowingRangeWarning = true
      this.showRangeWarningNotification()
      this.resetToDefaultDateRange()
      this.scheduleWarningReset()
    },

    showRangeWarningNotification() {
      notify({
        title: 'AVISO:',
        message: `Selecione um período de até ${TRACEABILITY_SEARCH_PERIOD_DAYS_DEFAULT} dias.`,
      })
    },

    resetToDefaultDateRange() {
      this.dateSelected = {
        start: this.defaultStartDate,
        end: this.defaultEndDate,
      }
    },

    scheduleWarningReset() {
      setTimeout(() => {
        this.isShowingRangeWarning = false
      }, 3000)
    },
  },
}
</script>

<style lang="scss" scoped>
.traceability-filter {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;

  div {
    flex: 1 1 auto;
    margin: 0;
  }
}

.search-select-options {
  max-width: 95%;
}
</style>
